import { token } from '@terra/ui-styled-system';
import type { ApexChartContext } from './format';

export type ChartTooltipTheme = 'light' | 'dark';

export type ChartTooltipStyles = {
  background: string;
  color: string;
  mutedColor: string;
};

export function getChartTooltipStyles(
  theme: ChartTooltipTheme = 'light',
): ChartTooltipStyles {
  if (theme === 'dark') {
    return {
      background: token('colors.white'),
      color: token('colors.neutral.light.12'),
      mutedColor: token('colors.neutral.dark.7'),
    };
  }

  return {
    background: token('colors.neutral.light.12'),
    color: token('colors.white'),
    mutedColor: token('colors.neutral.light.6'),
  };
}

function wrapChartTooltipHtml({
  styles,
  content,
}: {
  styles: ChartTooltipStyles;
  content: string;
}) {
  return `<div style="padding: 8px; background: ${styles.background}; border-radius: 4px; font-family: inherit; font-size: 12px; color: ${styles.color};">${content}</div>`;
}

export function formatDonutChartTooltip({
  theme,
  title,
  subtitle,
}: {
  theme: ChartTooltipTheme;
  title: string;
  subtitle: string;
}) {
  const styles = getChartTooltipStyles(theme);

  return wrapChartTooltipHtml({
    styles,
    content: `
      <div style="color: ${styles.color};">${title}</div>
      <div style="font-weight: 300; color: ${styles.mutedColor};">${subtitle}</div>
    `,
  });
}

export function formatLineChartTooltip({
  theme,
  series,
  dataPointIndex,
  w,
  categories,
}: {
  theme: ChartTooltipTheme;
  series: number[][];
  dataPointIndex: number;
  w: ApexChartContext;
  categories: string[];
}) {
  const styles = getChartTooltipStyles(theme);
  const category = categories[dataPointIndex] || '';

  const seriesRows = series
    .map(
      (s: number[], index: number) => `
    <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px; color: ${styles.mutedColor};">
      <div style="width: 6px; height: 6px; border-radius: 50%; background: ${w.globals.colors?.[index] ?? ''};"></div>
      <span>${w.globals.seriesNames?.[index] ?? ''} ${s?.[dataPointIndex] ?? 0}</span>
    </div>
  `,
    )
    .join('');

  return wrapChartTooltipHtml({
    styles,
    content: `
      <div style="color: ${styles.color}; margin-bottom: 8px;">${category}</div>
      ${seriesRows}
    `,
  });
}
